<template>
  <FormContainer :title="$t('title.company')" :onSubmit="onSubmit">
    <div class="columns">
      <div class="column">
        <FormItem v-if="role.admin && (!data.id || data.parentId)">
          <b-select
            expanded
            v-model="data.parentId"
            name="mainCompany"
            @input="parentIdChanged(data.parentId)"
          >
            <option :value="null">{{ `--${$t('title.selectOption')}--` }}</option>
            <option v-for="option in mainCompanies" :value="option.id" :key="option.id">
              {{ option.name }}
            </option>
          </b-select>
        </FormItem>
        <FormItem>
          <b-input v-model="data.name" v-validate="'required'" name="name" expanded />
        </FormItem>
        <FormItem>
          <b-input v-model="data.email" v-validate="'required'" name="email" expanded />
        </FormItem>
        <div class="columns" style="padding-top:20px;" v-if="!data.parentId && role.admin">
          <div class="column">
            <FormItem>
              <b-datepicker
                v-model="data.membershipEndDate"
                name="membershipEndDate"
                editable
                :min-date="new Date()"
                :first-day-of-week="dateSettings.firstDayOfWeek"
                :month-names="dateSettings.monthNames"
                :day-names="dateSettings.dayNames"
              ></b-datepicker>
            </FormItem>
          </div>
          <div class="column">
            <b>{{ $t('title.permissions') }}</b>
            <section style="padding-top:20px;">
              <div class="field">
                <b-checkbox
                  v-model="processCreatePermission"
                  true-value="processCreate"
                  :false-value="null"
                  >{{ $t('description.processCreatePermission') }}</b-checkbox
                >
              </div>
              <div class="field">
                <b-checkbox
                  v-model="companyManagementPermission"
                  true-value="subCompanyManagement"
                  :false-value="null"
                  >{{ $t('description.subCompanyManagementPermission') }}</b-checkbox
                >
              </div>
              <div class="field">
                <b-checkbox
                  v-model="defaultProcessPermission"
                  true-value="subCompanySeeDefaultProcesses"
                  :false-value="null"
                  >{{ $t('description.subCompanySeeDefaultProcessesPermission') }}</b-checkbox
                >
              </div>
              <div class="field">
                <b-checkbox
                  v-model="pointSystemforDepartment"
                  true-value="processWithPoint"
                  :false-value="null"
                  >{{ $t('description.pointSystemforDepartment') }}</b-checkbox
                >
              </div>
            </section>
          </div>
        </div>
      </div>
    </div>
  </FormContainer>
</template>

<script>
import { mapGetters } from 'vuex'
import dateSettings from '@/_helpers/date.config'
import ApiService from '../../../_api/api.service'

export default {
  props: ['id'],
  data () {
    return {
      data: {},
      mainCompanies: [],
      processCreatePermission: null,
      companyManagementPermission: null,
      defaultProcessPermission: null,
      pointSystemforDepartment: null,
      dateSettings
    }
  },
  computed: {
    ...mapGetters(['currentUser', 'role'])
  },
  mounted () {
    if (this.role.admin) {
      this.getMainCompanies()
    }
    if (this.id) {
      this.getData()
    } else if (this.role.company) {
      this.data.parentCompanyId = this.currentUser.companyId
    }
  },
  methods: {
    getMainCompanies () {
      ApiService.query('/select/maincompanies').then(data => {
        this.mainCompanies = data
      })
    },
    getData () {
      ApiService.get(`/company/${this.id}`).then(data => {
        // eslint-disable-next-line no-param-reassign
        if (this.role.admin && data.membershipEndDate) {
          data.membershipEndDate = new Date(Date.parse(data.membershipEndDate))
        } else if (this.role.admin && !data.membershipEndDate) {
          data.membershipEndDate = null
        }

        this.data = data

        if (this.role.admin) {
          this.loadPermissionsCheckbox()
        }
      })
    },
    parentIdChanged (parentId) {
      if (parentId == null) this.data.membershipEndDate = null
    },
    async onSubmit () {
      if (!this.data.parentId && this.role.admin) {
        this.data.permissions = this.getCompanyPermissions()
      }
      if (this.data.parentId) {
        this.data.membershipEndDate = this.mainCompanies.map(item => item.membershipEndDate)[0]
      }

      if (!this.data.id) {
        await this.checkNewCompanyEmailAndSave(this.data.email)
      } else {
        this.saveCompany()
      }
    },
    saveCompany({ hasTransferredUser } = {}) {
      const url = `${window.location.origin}/${this.$t('routes.user')}/${this.$t('routes.signup')}`

      ApiService.post('/company', { company: this.data, url, hasTransferredUser }).then(data => {
        this.data = data

        this.$emit('saved')
        this.$parent.close()
      })
    },
    async checkNewCompanyEmailAndSave (email) {
      const emailResult = await ApiService.put('/company/checkemail', { email })

      if (emailResult.isRegistered && (!emailResult.isAvailableForTransfer || !this.role.admin)) {
        this.$buefy.toast.open({
          message: this.$t('description.emailAlreadyRegistered'),
          type: 'is-danger'
        })
      } else if (emailResult.isRegistered && emailResult.isAvailableForTransfer && this.role.admin) {
        this.$buefy.dialog.confirm({
          message: this.$t('description.transferUserToNewCompany'),
          confirmText: this.$t('button.yes'),
          cancelText: this.$t('button.no'),
          type: 'is-warning',
          onConfirm: async () => {
            this.saveCompany({ hasTransferredUser: true })
          }
        })
      } else {
        this.saveCompany()
      }
    },
    getCompanyPermissions () {
      const permissions = [
        this.processCreatePermission,
        this.companyManagementPermission,
        this.defaultProcessPermission,
        this.pointSystemforDepartment
      ]
      return permissions.filter(p => p != null).join(',')
    },
    loadPermissionsCheckbox () {
      if (!this.data.permissions) return
      const permissionList = this.data.permissions.split(',')
      permissionList.forEach(permission => {
        if (permission === 'processCreate') {
          this.processCreatePermission = permission
        } else if (permission === 'subCompanyManagement') {
          this.companyManagementPermission = permission
        } else if (permission === 'subCompanySeeDefaultProcesses') {
          this.defaultProcessPermission = permission
        } else if (permission === 'processWithPoint') {
          this.pointSystemforDepartment = permission
        }
      })
    }
  }
}
</script>
